import React from 'react'
import { Box, MenuItem, TextField, Typography } from '@material-ui/core'
import { kitLabelOf } from '../model/workflow'
import { chemistryTag, ownCallingUnavailableReason } from 'src/utils/bedChemistry'

// The capture kit picker of the workflow column. The menu opens under the field,
// left-aligned and bounded, so a long kit list or name never runs off the panel.

const uploadDate = (value) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}

/**
 * Kit options from the /bed list: the name without a trailing ".bed", the full name
 * as a title, and the upload date when two kits would otherwise read the same.
 * As the wizard's BedPanelSelect, only a verified kit can be chosen. A kit whose
 * library chemistry Genesus cannot analyse (own_calling.available false) cannot be
 * chosen either, and carries the server's reason. A chemistry other than hybrid
 * capture is named as a small tag.
 * @returns {Array<{ value, label, title, reference, uploaded, disabledReason, chemistry, unavailableReason }>}
 */
export const kitMenuOptions = (bedFiles) => {
  const list = (Array.isArray(bedFiles) ? bedFiles : []).map((bed) => {
    const full = bed.visible_name || bed.original_name || bed.name || ''
    const processed = bed.is_processed !== false
    const verified = Boolean(bed.is_verified)
    const unavailableReason = ownCallingUnavailableReason(bed)
    return {
      value: bed.name,
      label: kitLabelOf(bed),
      title: full,
      reference: bed.reference,
      created: bed.created,
      disabledReason: !processed ? 'Verifying' : !verified ? 'Unverified' : '',
      chemistry: chemistryTag(bed.chemistry),
      unavailableReason,
    }
  })
  const counts = {}
  list.forEach((o) => {
    const k = o.label.toLowerCase()
    counts[k] = (counts[k] || 0) + 1
  })
  return list.map(({ created, ...o }) => ({
    ...o,
    uploaded: counts[o.label.toLowerCase()] > 1 ? uploadDate(created) : '',
  }))
}

export const KIT_MENU_PROPS = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
  transformOrigin: { vertical: 'top', horizontal: 'left' },
  PaperProps: { sx: { maxWidth: 440, maxHeight: 360, mt: 0.5 } },
}

const ELLIPSIS = { minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }

/**
 * @param options kitMenuOptions(bedFiles)
 * @param fallbackLabel shown for a chosen kit that is not in the list (workflow.kitName)
 * @param placeholder shown while nothing is chosen
 * @param emptyLabel when given, the first item: a choosable "none" ('') with this label
 * @param helperText shown under the field
 */
const KitSelect = ({
  value,
  options = [],
  onChange,
  error,
  fallbackLabel,
  placeholder = 'Choose a kit',
  emptyLabel,
  helperText,
  sx,
}) => (
  <TextField
    select
    size="small"
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    error={error}
    helperText={helperText}
    SelectProps={{
      displayEmpty: true,
      MenuProps: KIT_MENU_PROPS,
      renderValue: (selected) =>
        selected ? (
          <Box component="span" sx={{ display: 'block', ...ELLIPSIS }}>
            {options.find((k) => k.value === selected)?.label || fallbackLabel || selected}
          </Box>
        ) : (
          <Typography component="span" sx={{ fontSize: 14.5, color: 'text.secondary' }}>
            {placeholder}
          </Typography>
        ),
    }}
    sx={sx}
  >
    {emptyLabel && (
      <MenuItem value="" sx={{ fontSize: 14.5 }}>
        {emptyLabel}
      </MenuItem>
    )}
    {options.length === 0 && !emptyLabel && (
      <MenuItem value="" disabled sx={{ fontSize: 14.5 }}>
        No kits yet
      </MenuItem>
    )}
    {options.map((kit) => {
      const aside = [kit.chemistry, kit.uploaded, kit.disabledReason || kit.reference].filter(Boolean).join(' · ')
      const disabled = Boolean(kit.disabledReason || kit.unavailableReason)
      return (
        <MenuItem
          key={kit.value}
          value={kit.value}
          disabled={disabled}
          title={kit.unavailableReason || kit.title}
          sx={{ fontSize: 14.5, display: 'block' }}
        >
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'space-between', alignItems: 'baseline' }}>
            <Box component="span" sx={ELLIPSIS}>
              {kit.label}
            </Box>
            {aside && (
              <Typography component="span" sx={{ fontSize: 12.5, color: 'text.secondary', flexShrink: 0 }}>
                {aside}
              </Typography>
            )}
          </Box>
          {kit.unavailableReason && (
            <Typography
              component="span"
              data-testid="kit-unavailable-reason"
              sx={{ display: 'block', fontSize: 12.5, color: 'text.secondary', whiteSpace: 'normal', mt: 0.25 }}
            >
              {kit.unavailableReason}
            </Typography>
          )}
        </MenuItem>
      )
    })}
  </TextField>
)

export default KitSelect
